import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// Per-game read watermark for the feedback inbox.
//
// One timestamp per game (see migration 0021). An activity entry is
// "unread" when its lastActivityAt sits strictly past lastReadAt; when
// lastReadAt is unknown (first load), we treat everything as read so
// the UI doesn't flash dots before we know the truth - the alternative
// (treating everything as unread) lights up the sidebar on every page
// load until the query lands, which is worse.
//
// markRead is monotonic on both sides: the SQL upsert uses greatest(),
// and the local state set is gated on the new value being strictly
// later. That means callers can fire-and-forget without worrying about
// out-of-order requests dragging the watermark back.
// ---------------------------------------------------------------------------

const ReadRowSchema = z.object({
  game_id: z.string(),
  last_read_at: z.string(),
});

export type FeedbackInboxRead = {
  lastReadAt: string | null;
  isUnread: (lastActivityAt: string) => boolean;
  markRead: (atMost: string) => Promise<void>;
};

export function useFeedbackInboxRead(gameId: string | null): FeedbackInboxRead {
  const [lastReadAt, setLastReadAt] = useState<string | null>(null);

  // Initial fetch + identity reset on game change. We deliberately set
  // back to null on game change so the predicate doesn't briefly use
  // the previous game's watermark against the new game's activity.
  useEffect(() => {
    if (gameId === null) {
      setLastReadAt(null);
      return;
    }
    let cancelled = false;
    setLastReadAt(null);
    void supabase
      .from('feedback_inbox_read')
      .select('game_id, last_read_at')
      .eq('game_id', gameId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const parsed = data === null ? null : ReadRowSchema.safeParse(data);
        if (parsed === null) {
          // No row yet - the game has never had its inbox read. We
          // intentionally keep lastReadAt null so isUnread treats
          // everything as read until a real watermark arrives. The
          // first markRead() will create the row.
          return;
        }
        if (parsed.success) setLastReadAt(parsed.data.last_read_at);
      });
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  // Monotonic local advance. Mirrors the SQL greatest() so other-tab
  // realtime updates and our own optimistic updates can't regress the
  // watermark.
  const advance = useCallback((next: string): void => {
    setLastReadAt((current) => {
      if (current === null) return next;
      return next > current ? next : current;
    });
  }, []);

  // Realtime: another tab/user advancing the watermark needs to clear
  // dots here within a beat. Mirrors the self-heal pattern in
  // use-feedback-inbox.ts (visibility rejoin + exponential backoff).
  useEffect(() => {
    if (gameId === null) return;
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectDelay = 1_000;
    let lastStatus: string | null = null;

    function teardownChannel(): void {
      if (channel !== null) {
        // removeChannel (rather than unsubscribe) drops the channel
        // from supabase-js's internal name->channel registry. Without
        // this, React StrictMode's effect double-invoke calls
        // supabase.channel(SAME_NAME) the second time and gets back
        // the already-subscribed instance, so registering a new
        // postgres_changes listener throws "cannot add callbacks
        // after subscribe()".
        void supabase.removeChannel(channel);
        channel = null;
      }
    }

    function applyPayload(row: unknown): void {
      const parsed = ReadRowSchema.safeParse(row);
      if (!parsed.success) return;
      advance(parsed.data.last_read_at);
    }

    function connect(): void {
      if (cancelled) return;
      teardownChannel();
      const next = supabase
        .channel(`feedback-inbox-read:${gameId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'feedback_inbox_read',
            filter: `game_id=eq.${gameId}`,
          },
          (payload) => {
            applyPayload(payload.new);
          },
        );
      channel = next;
      next.subscribe((status) => {
        if (cancelled) return;
        lastStatus = status;
        if (status === 'SUBSCRIBED') {
          reconnectDelay = 1_000;
          if (reconnectTimer !== null) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
          return;
        }
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          if (reconnectTimer !== null) return;
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
            connect();
          }, reconnectDelay);
        }
      });
    }

    function onVisibility(): void {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      if (lastStatus !== 'SUBSCRIBED') {
        if (reconnectTimer !== null) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        reconnectDelay = 1_000;
        connect();
      }
    }

    document.addEventListener('visibilitychange', onVisibility);
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      teardownChannel();
    };
  }, [gameId, advance]);

  // Stable predicate that always reads the latest watermark via ref.
  // Otherwise consumers like ActivityPanel would re-render on every
  // upstream update only because the function identity changed.
  const lastReadAtRef = useRef<string | null>(lastReadAt);
  lastReadAtRef.current = lastReadAt;
  const isUnread = useCallback((lastActivityAt: string): boolean => {
    const watermark = lastReadAtRef.current;
    if (watermark === null) return false;
    return lastActivityAt > watermark;
  }, []);

  const markRead = useCallback(
    async (atMost: string): Promise<void> => {
      if (gameId === null) return;
      // Local short-circuit: skip the RPC when the watermark is already
      // at or past the proposed value. Avoids a network round-trip
      // every time the user clicks an old activity item.
      const current = lastReadAtRef.current;
      if (current !== null && atMost <= current) return;
      // Optimistic advance so the dot clears immediately.
      advance(atMost);
      const { data, error } = await supabase.rpc('upsert_feedback_inbox_read', {
        p_game_id: gameId,
        p_at: atMost,
      });
      if (error !== null) {
        // No rollback - if the RPC fails the next realtime update or
        // page load will re-resolve the truth. Logging is enough.
        console.warn('[feedback-inbox-read] upsert failed', error);
        return;
      }
      if (typeof data === 'string') advance(data);
    },
    [gameId, advance],
  );

  return { lastReadAt, isUnread, markRead };
}
